import { z } from "zod";

import {
  formulaTransformSchema,
  plotYAxisSchema,
} from "@/shared/state/visualization/schema/v1";
import {
  meshSchema,
  pathSchema,
  positionBasePresetSchema,
  positionTransformationSchema,
} from "@/shared/state/visualization/schema/v1/three-d";

import type { RuleStyle } from "../LayoutRecord";

// Mirrors ThreeDPositionDataMapping in ../LayoutRecord; that type documents
// what topicDataIndex and scale mean.
const threeDPositionDataMappingSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("topicData"),
    scale: z.number(),
    topicDataIndex: z.number(),
  }),
  z.object({
    type: z.literal("fixed"),
    scale: z.number(),
    value: z.number(),
  }),
]);

const threeDPositionStyleSchema = z.object({
  transformation: positionTransformationSchema.optional(),
  basePreset: positionBasePresetSchema.optional(),
  forward: threeDPositionDataMappingSchema,
  right: threeDPositionDataMappingSchema,
  up: threeDPositionDataMappingSchema,
});

/**
 * How a rule's matched data is drawn. One style serves every panel type, and a
 * panel reads only the fields it uses: a plot panel uses every field except
 * threeDComponents; a map panel uses color and label; a 3D panel uses
 * threeDComponents; log, image, document, and raw-message panels ignore the
 * style.
 *
 * Unknown keys pass validation instead of failing it, so a layout saved by a
 * newer application bundle still loads here.
 */
export const ruleStyleSchema: z.ZodType<RuleStyle> = z.object({
  color: z.string().optional(),
  lineWidth: z.number().optional(),
  pointRadius: z.number().optional(),
  yAxis: plotYAxisSchema.optional(),
  label: z.string().optional(),
  transform: formulaTransformSchema.optional(),
  threeDComponents: z
    .object({
      entityId: z.string(),
      position: threeDPositionStyleSchema.optional(),
      mesh: meshSchema.optional(),
      path: pathSchema.optional(),
    })
    .optional(),
});
